import React, { useState, useEffect } from 'react';
import Navigation from '../../components/Navigation';

const isDev =
  window.location.hostname === "localhost" ||
  window.location.hostname.includes("github");

const API_ORIGIN = "https://api.buildbright.app";

const CONTRACTORS_API = `${API_ORIGIN}/api/contractor`;
const LEADS_API = `${API_ORIGIN}/api/leads`;

function authHeaders() {
  const t = localStorage.getItem("bb_contractor_token");
  return t ? { "Content-Type": "application/json", "Authorization": `Bearer ${t}` }
           : { "Content-Type": "application/json" };
}

const Portal = () => {
  const formatPlanName = (plan) => {
    if (!plan) return 'Membership';
    return String(plan).charAt(0).toUpperCase() + String(plan).slice(1);
  };

  const displayClaimCredits = () => {
    if (credits?.credits_limit === null) return 'Unlimited';
    if (credits?.credits_remaining !== undefined && credits?.credits_remaining !== null) {
      return credits.credits_remaining;
    }
    return contractor?.credits_balance || 0;
  };

  const [activeTab, setActiveTab] = useState('leads');
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilters, setLeadsFilters] = useState({
    status: '',
    service: '',
    q: ''
  });
  
  // Billing state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const caRequirementsReviewed = !!contractor?.ca_requirements_reviewed;
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  
  // Settings state
  const [profileData, setProfileData] = useState({
    company: '',
    phone: '',
    services: [],
    service_area: { zips: [] },
    notifications: { email: true, sms: false }
  });

  const hasProfileServices = (profileData.services?.length || contractor?.services?.length || 0) > 0;
  const hasProfileZips = (profileData.service_area?.zips?.length || contractor?.service_area?.zips?.length || 0) > 0;
  const needsProfileSetup = !hasProfileServices || !hasProfileZips;
  
  // Profile/Onboarding state
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaveMessage, setProfileSaveMessage] = useState('');

  // Credits state
  const [credits, setCredits] = useState(null);
  const [claimingLead, setClaimingLead] = useState(null);
  const [leadToUnlock, setLeadToUnlock] = useState(null);

  // Billing portal state
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  const formatLeadAge = (createdAt) => {
    if (!createdAt) return { text: 'Unknown', label: 'Lead', tone: 'text-zinc-400' };

    const created = new Date(createdAt);
    const diffMs = Date.now() - created.getTime();
    const diffMin = Math.max(0, Math.floor(diffMs / 60000));
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMin < 60) return { text: `${diffMin || 1} min ago`, label: 'New', tone: 'text-orange-400' };
    if (diffHours < 24) return { text: `${diffHours} hr ago`, label: 'Fresh', tone: 'text-green-400' };
    if (diffDays < 2) return { text: 'Yesterday', label: 'Recent', tone: 'text-blue-400' };
    return { text: `${diffDays} days ago`, label: 'Older', tone: 'text-zinc-400' };
  };

  
  const availableServices = ['roofing', 'solar', 'landscaping'];

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check localStorage first
      const cachedToken = localStorage.getItem("bb_contractor_token");
      const cachedContractor = localStorage.getItem("bb_contractor");
      if (cachedContractor && cachedToken) {
        try {
          const c = JSON.parse(cachedContractor);
          setContractor(c);
          setProfileData({
            company: c.company || "",
            phone: c.phone || "",
            services: c.services || [],
            service_area: c.service_area || { zips: [] },
            notifications: c.notifications || { email: true, sms: false }
          });
        } catch(e) {}
      }
      // Fallback to API check
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const authToken = localStorage.getItem("bb_contractor_token");
      const response = await fetch(`${API_ORIGIN}/api/contractor/me`, {
        credentials: "include",
        headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {},
        signal: controller.signal,
        cache: "no-store",
      });


      clearTimeout(timeoutId);

      if (response.status === 401) {
        localStorage.removeItem("bb_contractor_token");
        localStorage.removeItem("bb_contractor");
        window.location.href = '/#/contractors/login';
        return;
      }

      if (response.ok) {
        const result = await response.json().catch(() => ({}));
        const ok = result?.ok === true || result?.success === true;

        if (!ok || !result?.contractor) {
          window.location.href = '/#/contractors/login';
          return;
        }

        setContractor(result.contractor);
        localStorage.setItem("bb_contractor", JSON.stringify(result.contractor));
        setProfileData({
          company: result.contractor.company || '',
          phone: result.contractor.phone || '',
          services: result.contractor.services || [],
          service_area: result.contractor.service_area || { zips: [] },
          notifications: result.contractor.notifications || { email: true, sms: false }
        });

        return;
      }

      setError('Failed to load profile');
      return;

    } catch (error) {
      console.error('Auth check error:', error);
      if (error.name === 'AbortError') {
        setError('Connection timeout - API unreachable');
      } else {
        setError('Network error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams();
      if (leadsFilters.status) params.append('status', leadsFilters.status);
      if (leadsFilters.service) params.append('service', leadsFilters.service);
      if (leadsFilters.q) params.append('q', leadsFilters.q);

      const authToken = localStorage.getItem("bb_contractor_token");
      const response = await fetch(
        `${CONTRACTORS_API}/leads?${params.toString()}`,
        { credentials: "include", headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {} }
      );

      if (response.ok) {
        const result = await response.json().catch(() => ({}));
        const ok = result?.ok === true || result?.success === true;

        if (ok) {
          setLeads(result.leads || []);
        }
      }
    } catch (error) {
      console.error('Load leads error:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const claimLead = async (leadId) => {
  try {
    const authToken = localStorage.getItem("bb_contractor_token");
    setClaimingLead(leadId);
    const response = await fetch(`${CONTRACTORS_API}/leads/${leadId}/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
      },
      credentials: 'include',
    });

    if (response.status === 401) {
      alert('Session expired. Please log in again.');
      window.location.href = '/#/contractors/login';
      return;
    }

    const result = await response.json().catch(() => ({}));
    const ok = result?.ok === true || result?.success === true;

    if (ok) {
      alert('Project details unlocked! Contact: ' + (result.contact?.phone || result.contact?.name || 'Check portal'));
      loadLeads();
      loadCredits();
      setLeadToUnlock(null);
      return;
    }

    const errMsg = result?.detail?.message || result?.detail || result?.message || 'Failed to unlock project details';
    alert(errMsg);
  } catch (error) {
    console.error('Claim lead error:', error);
    alert('Network error while unlocking project details');
  } finally {
    setClaimingLead(null);
  }
};

  const refundLead = async (leadId) => {
    try {
      const authToken = localStorage.getItem("bb_contractor_token");
      const reason = window.prompt('Refund reason (required):', 'Invalid contact data');
      if (!reason) return;

      const response = await fetch(`${LEADS_API}/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ lead_id: leadId, reason, credits: 1 }),
      });

      if (response.status === 401) {
        alert('Session expired. Please log in again.');
        window.location.href = '/#/contractors/login';
        return;
      }

      const result = await response.json().catch(() => ({}));
      const ok = result?.ok === true || result?.success === true;

      if (ok) {
        alert('Refund submitted. Credits restored.');
        loadLeads();
        loadCredits();
        return;
      }

      const errMsg = result?.detail?.message || result?.detail || result?.message || 'Failed to refund lead';
      alert(errMsg);
    } catch (error) {
      console.error('Refund lead error:', error);
      alert('Network error while refunding lead');
    }
  };

  const exportLeads = async () => {
    try {
      const response = await fetch(`${LEADS_API}/export.csv`, {
        credentials: "include",
      });


      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'leads_export.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to export leads');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Network error');
    }
  };

  const logout = async () => {
    try {
      await fetch(`${CONTRACTORS_API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      window.location.href = '/#/contractors/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileSaveMessage('');
    
    try {
      const response = await fetch(`${CONTRACTORS_API}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          services: profileData.services,
          service_area: profileData.service_area
        })
      });
      
      if (response.ok) {
        setProfileSaveMessage('Profile saved successfully!');
        // Refresh contractor data
        await checkAuth();
        // Refresh leads
        if (activeTab === 'leads') {
          loadLeads();
        }
      } else {
        const err = await response.json().catch(() => ({}));
        setProfileSaveMessage(err?.detail || err?.message || `Failed to save profile (${response.status})`);
      }

    } catch (error) {
      console.error('Save profile error:', error);
      setProfileSaveMessage('Network error');
    } finally {
      setSavingProfile(false);
      setTimeout(() => setProfileSaveMessage(''), 3000);
    }
  };

  const handleServiceToggle = (service) => {
    const updatedServices = profileData.services.includes(service)
      ? profileData.services.filter(s => s !== service)
      : [...profileData.services, service];
    
    setProfileData({
      ...profileData,
      services: updatedServices
    });
  };

  const handleZipsChange = (value) => {
    const zips = value.split(',').map(z => z.trim()).filter(z => z);
    setProfileData({
      ...profileData,
      service_area: { zips }
    });
  };

  const loadCredits = async () => {
    try {
      const authToken = localStorage.getItem("bb_contractor_token");
      const res = await fetch(`${CONTRACTORS_API}/credits`, {
        credentials: "include",
        headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setCredits(data);
      }
    } catch (e) {}
  };


  useEffect(() => {
    if (!contractor) return;

    if (activeTab === 'leads') {
      loadLeads();
      loadCredits();
    }

    if (activeTab === 'billing') {
      loadCredits();
    }
  }, [activeTab, leadsFilters, contractor]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="space-y-2">
            <button 
              onClick={() => {
                setError('');
                setLoading(true);
                checkAuth();
              }} 
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded mr-2"
              data-testid="auth-retry"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.href = '/#/contractors/login'} 
              className="text-zinc-300 hover:text-zinc-200 px-4 py-2"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="portal-root">
      <Navigation />
      
      {/* Portal Header */}
      <div className="bg-black shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Contractor Portal</h1>
              {caRequirementsReviewed && (
                <div className="mt-2 inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                  Requirements Reviewed
                </div>
              )}

              <p className="text-zinc-300">Welcome, {contractor?.name}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-zinc-300">
                Claim Credits: <span className="font-semibold">{displayClaimCredits()}</span>
              </div>
              <button
                onClick={logout}
                className="text-zinc-300 hover:text-white"
              >
                Logout
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-zinc-800">
            <nav className="-mb-px flex space-x-8">
              {['leads', 'billing', 'settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {leadToUnlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-950 p-6 shadow-2xl">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-400">
                Confirm unlock
              </p>
              <h2 className="mt-1 text-xl font-bold text-white">
                Unlock homeowner details?
              </h2>
              <p className="mt-2 text-sm text-zinc-300">
                This will use 1 claim credit to reveal the homeowner’s phone/email for this qualified project opportunity.
              </p>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-black p-4 text-sm text-zinc-300">
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Service</span>
                <span className="text-white text-right">{leadToUnlock.serviceType || leadToUnlock.service || leadToUnlock.contact?.service || 'Home Service'}</span>
              </div>
              <div className="mt-2 flex justify-between gap-4">
                <span className="text-zinc-500">Location</span>
                <span className="text-white text-right">{leadToUnlock.zip || leadToUnlock.zip_code || 'Available after unlock'}</span>
              </div>
              <div className="mt-2 flex justify-between gap-4">
                <span className="text-zinc-500">Claim credit use</span>
                <span className="text-white text-right">1 credit</span>
              </div>
            </div>

            <p className="mt-4 text-xs text-zinc-500">
              Unlock only when the project preview looks like a fit. Project outcome, homeowner response, pricing, and scheduling are not guaranteed.
            </p>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setLeadToUnlock(null)}
                className="rounded-xl border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-300 hover:bg-zinc-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => claimLead(leadToUnlock.id)}
                disabled={claimingLead === leadToUnlock.id}
                className="rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {claimingLead === leadToUnlock.id ? 'Unlocking...' : 'Unlock Details — Use 1 Credit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'leads' && (
          <div data-testid="leads-panel">
            {/* Welcome / Next Steps */}
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-5 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange-400">Contractor access</p>
                  <h2 className="text-xl font-bold text-white mt-1">
                    Welcome to your BuildBright project opportunity portal
                  </h2>
                  <p className="text-sm text-zinc-300 mt-2 max-w-3xl">
                    Start by confirming your services and ZIP coverage. BuildBright will show qualified project opportunity previews when they match your profile and service area.
                  </p>
                </div>
                <div className="rounded-lg bg-black border border-zinc-700 p-4 min-w-[180px] text-center">
                  <div className="text-2xl font-bold text-white">{displayClaimCredits()}</div>
                  <div className="text-xs text-zinc-400 mt-1">Available claim credits</div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl border border-zinc-700 bg-black p-4">
                  <div className="text-orange-400 font-semibold">1. Confirm coverage</div>
                  <p className="text-zinc-400 mt-1">Add the services and ZIP codes your company wants to review.</p>
                </div>
                <div className="rounded-xl border border-zinc-700 bg-black p-4">
                  <div className="text-orange-400 font-semibold">2. Review previews</div>
                  <p className="text-zinc-400 mt-1">Check project type, area, urgency, and notes before unlocking details.</p>
                </div>
                <div className="rounded-xl border border-zinc-700 bg-black p-4">
                  <div className="text-orange-400 font-semibold">3. Unlock by choice</div>
                  <p className="text-zinc-400 mt-1">Use claim credits only when a project looks like a fit.</p>
                </div>
              </div>
            </div>

            {/* Profile/Onboarding Section */}
            <div className="bg-black rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">Profile / Onboarding</h3>
              
              {needsProfileSetup && (
                <div className="bg-orange-50 border border-yellow-200 rounded-md p-4 mb-4">
                  <p className="text-orange-800 text-sm">
                    📝 Add services + zip codes to start seeing project opportunities.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-3">
                    Services (Select all that apply)
                  </label>
                  <div className="space-y-2">
                    {availableServices.map(service => (
                      <label key={service} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={profileData.services.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-zinc-700 rounded"
                          data-testid={`service-${service}`}
                        />
                        <span className="ml-2 text-sm text-zinc-200 capitalize">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">
                    Service Area ZIP Codes
                  </label>
                  <input
                    type="text"
                    value={profileData.service_area.zips?.join(', ') || ''}
                    onChange={(e) => handleZipsChange(e.target.value)}
                    placeholder="90012, 90210, 91001"
                    className="w-full border border-zinc-700 rounded-md px-3 py-2 mb-2"
                    data-testid="input-service-zips"
                  />
                  <p className="text-xs text-zinc-400">Comma separated (e.g., 90012, 90210)</p>
                </div>
              </div>
              
              <div className="mt-4 flex items-center gap-4">
                <button
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-md"
                  data-testid="btn-save-profile"
                >
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </button>
                
                {profileSaveMessage && (
                  <span className={`text-sm ${profileSaveMessage.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                    {profileSaveMessage}
                  </span>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="bg-black rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">Status</label>
                  <select
                    value={leadsFilters.status}
                    onChange={(e) => setLeadsFilters({ ...leadsFilters, status: e.target.value })}
                    className="w-full border border-zinc-700 rounded-md px-3 py-2"
                  >
                    <option value="">All Statuses</option>
                    <option value="new">New</option>
                    <option value="claimed">Claimed</option>
                    <option value="working">Working</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">Service</label>
                  <select
                    value={leadsFilters.service}
                    onChange={(e) => setLeadsFilters({ ...leadsFilters, service: e.target.value })}
                    className="w-full border border-zinc-700 rounded-md px-3 py-2"
                  >
                    <option value="">All Services</option>
                    <option value="roofing">Roofing</option>
                    <option value="landscaping">Landscaping</option>
                    <option value="solar">Solar</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">Search</label>
                  <input
                    type="text"
                    value={leadsFilters.q}
                    onChange={(e) => setLeadsFilters({ ...leadsFilters, q: e.target.value })}
                    placeholder="City, ZIP, keywords..."
                    className="w-full border border-zinc-700 rounded-md px-3 py-2"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={exportLeads}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
                    data-testid="btn-export-csv"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
            </div>

            {/* Membership + Claim Credits Banner */}
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-5 mb-4">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div>
                  <div className="text-white font-semibold">
                    {contractor?.subscription?.status === 'active'
                      ? `${formatPlanName(contractor?.subscription?.plan)} Active`
                      : 'Contractor Marketplace Access'}
                  </div>
                  <p className="text-sm text-zinc-300 mt-2 max-w-3xl">
                    You are viewing qualified project opportunity previews filtered by service area, project type, and BuildBright quality checks. Claim credits are used only when you choose to unlock a homeowner’s full contact details.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-zinc-800 border border-zinc-700 px-3 py-1 text-zinc-300">Filtered opportunities</span>
                    <span className="rounded-full bg-zinc-800 border border-zinc-700 px-3 py-1 text-zinc-300">Local match</span>
                    <span className="rounded-full bg-zinc-800 border border-zinc-700 px-3 py-1 text-zinc-300">Preview before unlock</span>
                  </div>
                </div>

                <div className="min-w-[180px] rounded-lg bg-black border border-zinc-700 p-4 text-center">
                  <div className="text-2xl font-bold text-white">
                    {displayClaimCredits()}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">Available claim credits</div>
                  <a href="/#/contractors/pricing" className="inline-block mt-3 text-orange-400 text-sm hover:underline">
                    View plans →
                  </a>
                </div>
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-black rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full" data-testid="leads-table">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Preview</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-black divide-y divide-gray-200">
                    {leadsLoading ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600 mx-auto"></div>
                        </td>
                      </tr>
                    ) : leads.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-10 text-center">
                          <div className="max-w-xl mx-auto">
                            <div className="text-white font-semibold text-lg">No matching project opportunity previews yet.</div>
                            <p className="text-zinc-400 text-sm mt-2">
                              Your membership is active. BuildBright only shows project opportunity previews that match your selected services, ZIP coverage, and quality filters. Confirm your coverage or check back as new homeowner requests come in.
                            </p>
                            <button
                              onClick={() => setActiveTab('settings')}
                              className="mt-4 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md"
                            >
                              Update Service Area
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {(() => {
                              const age = formatLeadAge(lead.created_at);
                              return (
                                <div>
                                  <div className="font-medium">{age.text}</div>
                                  <div className={`text-xs ${age.tone}`}>{age.label}</div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {(lead.utm_source || lead.source || 'Website').toUpperCase()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {lead.serviceType || lead.service || lead.contact?.service || 'Home Service'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {lead.zip || lead.zip_code || ''}
                          </td>
                          <td className="px-6 py-4 text-sm text-zinc-300 max-w-xs">
                            {lead.notes ? (
                              <div className="line-clamp-2">
                                {lead.notes.length > 120 ? `${lead.notes.slice(0, 120)}...` : lead.notes}
                              </div>
                            ) : (
                              <span className="text-zinc-500 text-xs">No preview available</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            <div>
                              {lead.claimed_by_me ? (<><div className="font-medium">{lead.contact?.phone || 'N/A'}</div><div className="text-zinc-400 text-xs">{lead.contact?.email || ''}</div></>) : (<span className="text-zinc-500 text-xs">🔒 Unlock homeowner details</span>)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              lead.status === 'claimed' || lead.status === 'active' ? 'bg-green-100 text-green-800' :
                              lead.status === 'working' ? 'bg-blue-100 text-blue-800' :
                              lead.status === 'scheduled' ? 'bg-purple-100 text-purple-800' :
                              lead.status === 'won' ? 'bg-green-100 text-green-800' :
                              lead.status === 'lost' ? 'bg-red-100 text-red-800' :
                              'bg-zinc-900 text-white'
                            }`}>
                              {lead.status || 'new'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {lead.claim_state === 'closed' ? (
                              <span className="text-gray-400">Closed</span>
                            ) : lead.claimed_by_me || lead.claimed_by_contractor === contractor?.id || (Array.isArray(lead.claimed_by) && contractor?.id && lead.claimed_by.includes(contractor.id)) ? (
                              <div className="flex flex-col gap-2">
                                <span className="text-green-600">Claimed</span>
                                <span className="text-zinc-400 text-xs">
                                  Manage subscription in Stripe Billing Portal
                                </span>
                              </div>
                            ) : (
                              <button
                                onClick={() => setLeadToUnlock(lead)}
                                disabled={claimingLead === lead.id}
                                className="text-orange-600 hover:text-orange-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`btn-claim-${lead.id}`}
                              >
                                {claimingLead === lead.id ? 'Unlocking...' : 'Unlock Homeowner Details (1 credit)'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
  <div data-testid="billing-panel">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Subscription Status */}
      <div className="bg-black rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          {contractor?.subscription?.status === 'active'
            ? `${formatPlanName(contractor?.subscription?.plan)} Active`
            : 'Subscription'}
        </h3>

        {contractor?.subscription?.status === 'active' && (
          <div className="mb-5 rounded-lg border border-green-700/40 bg-green-950/30 p-4">
            <p className="text-sm font-medium text-green-300">
              Your BuildBright contractor membership is active.
            </p>
            <p className="text-xs text-zinc-300 mt-2">
              You can now browse qualified local project opportunity previews. Claim credits are used separately when you want to unlock full homeowner contact details.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-zinc-300">Membership plan:</span>
            <span className="font-medium capitalize">
              {contractor?.subscription?.plan ? formatPlanName(contractor.subscription.plan) : 'No active plan'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-300">Membership status:</span>
            <span
              className={`font-medium ${
                contractor?.subscription?.status === 'active'
                  ? 'text-green-600'
                  : 'text-orange-600'
              }`}
            >
              {contractor?.subscription?.status || 'Inactive'}
            </span>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={() => (window.location.href = '/#/contractors/pricing')}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md"
            data-testid="btn-change-plan"
          >
            View Plans
          </button>
        </div>
      </div>

      {/* Credits Balance */}
      <div className="bg-black rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Claim Credits</h3>
        <div className="text-center">
          <div className="text-3xl font-bold text-white" data-testid="credits-balance">
            {displayClaimCredits()}
          </div>
          <div className="text-zinc-300">Available claim credits</div>
        </div>

        <div className="mt-4 rounded-lg border border-zinc-700 bg-zinc-900/60 p-4">
          <p className="text-xs text-zinc-300">
            Claim credits are separate from your monthly membership. Use 1 claim credit when you’re ready to unlock a homeowner’s phone/email and follow up on a qualified project.
          </p>
        </div>

        <div className="mt-6 rounded-lg border border-zinc-700 bg-zinc-900/60 p-4 text-sm text-zinc-300">
          Credits are added by plan changes or support.
          <div className="mt-2">
            Need help now? <a className="underline text-orange-400" href="mailto:support@buildbright.app">Contact support</a>.
          </div>
        </div>
      </div>

      {/* Billing Portal */}
      <div className="bg-black rounded-lg shadow p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold text-white mb-4">Billing Management</h3>
        <p className="text-zinc-300 mb-4">
          Manage your billing, view invoices, update payment methods, and cancel your subscription.
        </p>

        <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-4">
          <p className="text-sm text-zinc-300 mb-4">
            Access the Stripe Billing Portal to manage all billing details, payment methods, and subscription settings.
          </p>
          {portalError && (
            <div className="mb-4 rounded-lg border border-red-700/40 bg-red-950/30 p-3">
              <p className="text-sm text-red-300">{portalError}</p>
            </div>
          )}
          <button
            onClick={async () => {
              setPortalLoading(true);
              setPortalError('');
              try {
                const authToken = localStorage.getItem("bb_contractor_token");
                const response = await fetch(`${API_ORIGIN}/api/billing/portal`, {
                  method: 'POST',
                  headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {},
                  credentials: 'include'
                });

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({}));
                  throw new Error(errorData?.detail?.message || `Failed to access portal (${response.status})`);
                }

                const data = await response.json();
                if (data.url) {
                  window.location.href = data.url;
                } else {
                  throw new Error('No portal URL returned');
                }
              } catch (error) {
                console.error('Portal error:', error);
                setPortalError(error.message || 'Failed to access billing portal. Please try again.');
              } finally {
                setPortalLoading(false);
              }
            }}
            disabled={portalLoading}
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 text-white px-4 py-2 rounded-md font-medium"
            data-testid="btn-stripe-portal"
          >
            {portalLoading ? 'Opening Portal...' : 'Manage Billing in Stripe'}
          </button>
          <div className="mt-3 text-xs text-zinc-400">
            You'll be redirected to Stripe where you can cancel, pause, or update your subscription.
          </div>
        </div>
      </div>
    </div>
  </div>
)}

        {activeTab === 'settings' && (
          <div data-testid="settings-panel">
            <div className="bg-black rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Profile Settings</h3>
              
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-200 mb-1">Company Name</label>
                    <input
                      type="text"
                      value={profileData.company}
                      onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                      className="w-full border border-zinc-700 rounded-md px-3 py-2"
                      data-testid="input-company"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-200 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="w-full border border-zinc-700 rounded-md px-3 py-2"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">Service Areas (ZIP Codes)</label>
                  <input
                    type="text"
                    value={profileData.service_area.zips?.join(', ') || ''}
                    onChange={(e) => setProfileData({ 
                      ...profileData, 
                      service_area: { 
                        zips: e.target.value.split(',').map(z => z.trim()).filter(z => z) 
                      }
                    })}
                    placeholder="12345, 67890, 11111"
                    className="w-full border border-zinc-700 rounded-md px-3 py-2"
                    data-testid="input-zips"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-3">Notifications</label>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={profileData.notifications.email}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          notifications: { ...profileData.notifications, email: e.target.checked }
                        })}
                        className="h-4 w-4 text-orange-600 border-zinc-700 rounded"
                        data-testid="toggle-email"
                      />
                      <label className="ml-2 text-sm text-zinc-200">Email notifications</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={profileData.notifications.sms}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          notifications: { ...profileData.notifications, sms: e.target.checked }
                        })}
                        className="h-4 w-4 text-orange-600 border-zinc-700 rounded"
                        data-testid="toggle-sms"
                      />
                      <label className="ml-2 text-sm text-zinc-200">SMS notifications</label>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                       const response = await fetch(`${CONTRACTORS_API}/profile`, {
                         method: 'PATCH',
                         headers: { 'Content-Type': 'application/json' },
                         credentials: 'include',
                         body: JSON.stringify({
                         services: profileData.services,
                         service_area: profileData.service_area,
                         notifications: profileData.notifications,
                       }),
                     });
     
                        if (response.ok) {
                          alert('Profile updated successfully!');
                        } else {
                          const err = await response.json().catch(() => ({}));
                          alert(err?.detail || err?.message || `Failed to update profile (${response.status})`);
                        }
                      } catch (error) {
                        console.error('Update profile error:', error);
                        alert('Network error');
                      }
                    }}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-md"
                    data-testid="btn-save-settings"
                  >
                    Save Settings
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default Portal;
